import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import useDialogAccessibilityFeatures from 'shared-utils/src/useDialogAccessibilityFeatures'; 
import useVPVOnActivePanel from 'shared-utils/src/useVPVOnActivePanel';
import getQbWherePageType from 'shared-utils/src/getQbWherePageType';
import trackGTMEvents from 'shared-utils/src/trackGTMEvents/new';
import sleep from 'shared-utils/src/sleep';

import Spinner from 'shared-components/components/Spinner';

import { DRAWING_ACTIONS_MAP } from '@client/searchMap/constants';
import MapApiInitializer from '@client/searchMap/components/map/ApiLoader';
import Map from '@client/searchMap/components/map/Map';
import Buttons from '@client/searchMap/components/layout/Buttons';

import decorateMapLocality from '@client/searchMap/helpers/decorateMapLocality';

import { getConfigurationsBasedOnLocations } from './utils';

import getPerimeters from '../../../shared/getPerimeters';

import '../../../../../searchMap/style.scss';
import './style.scss';

const ExpandLocalityRadius = ({ queryBuilderProps, modalData }) => {
  const {
    isActivePanel,
    customStyle,
    updateModalSize,
    hiddenPanels,
    panel: { name: panelName },
    revealPanelContents,
    device,
    viewport,
    updateTempStateFromExpandRadiusPanel,
    updateTempSaveSearchStateFromMapPanel,
    mobileOs,
    isSafari,
    closeModal,
    panelId,
    headerTitleId,
    changeCurrentPanel,
    confirmTempStateAndGoBack,
    tempFiltersQB,
    tempAvailableResultsQB,
    tempLocationsQB: {
      searchLocations,
      searchLocationsParent,
    },
    locationPolygons,
    setLocationPolygons,
    confirmChoiceAndClosePanel,
    paPage,
  } = queryBuilderProps;

  const [opaque, setOpaque] = useState(false);
  const [componentIsActive, setComponentIsActive] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [changePolygonsIndex, setChangePolygonsIndex] = useState(0);

  useEffect(() => setChangePolygonsIndex((prev) => prev + 1), [locationPolygons]);

  useDialogAccessibilityFeatures(isActivePanel ? {
    elementId: panelId,
    isOpen: isActivePanel,
    closefn: () => closeModal(),
    customFocusableElements: [`#${headerTitleId}`],
  } : {});    

  useVPVOnActivePanel({
    isActivePanel,
    paPageDimensions: {
      page: paPage,
      cust_event_name: paPage,
      cust_page_type: getQbWherePageType({ locations: searchLocations, filters: tempFiltersQB }),
      cust_prev_page: window.paDatalayer?.page || '',
    },
  });  

  const updateQExpansion = async (newRadius) => {
    setIsFetching(true);
    const polygons = await getPerimeters({ selectedChildren: searchLocations, parent: searchLocationsParent, qExpansion: newRadius || '0' });
    setLocationPolygons(polygons);
    await updateTempStateFromExpandRadiusPanel(newRadius);
    setIsFetching(false);
  };

  useEffect(() => {
    const getInitialPolygons = async () => {
      await updateQExpansion(tempFiltersQB.qExpansion);
      setOpaque(false);
      setComponentIsActive(true);
    }

    if (isActivePanel && customStyle) {      
      setTimeout(() => {
        setOpaque(false);
        updateModalSize(customStyle);
        setTimeout(() => {
          setComponentIsActive(true);
        }, 305);
      }, 305);
    } else {
      getInitialPolygons();
    }
  }, [isActivePanel, customStyle]);

  useEffect(() => {
    if (hiddenPanels.includes(panelName) && !opaque) {
      setOpaque(true);
      setComponentIsActive(false);
      setTimeout(() => {
        revealPanelContents({ panel: panelName });
      }, 345);
    }
  }, [hiddenPanels, panelName, opaque]);

  const getAvailableResults = async ({ newFilters }) => {
    const newLocations = decorateMapLocality({});    
    const ar = updateTempStateFromExpandRadiusPanel(newLocations, newFilters);
    return ar;
  };

  const openQbFiltersPanel = () => changeCurrentPanel({ choosenPanel: 'filtersChild' });

  const center = { lat: 41.63287151440908, lng: 12.02197061016659 };
  const zoom = 6;
  const bounds = null;

  const confirmChoice = () => {
    setIsFetching(true);
    sleep(300).then(() => {
      setIsFetching(false);
    });
    trackGTMEvents({
      action: 'confirmSurroundings',
      label: tempFiltersQB.qExpansion || '0',
    });    
    if (customStyle) {
      if (device === 'smartphone') {
        confirmTempStateAndGoBack();
        return;
      }
      setTimeout(() => {
        modalData.restoreInitialSize();
        confirmTempStateAndGoBack();
      }, 125);
    } else {
      confirmChoiceAndClosePanel();
    }
  };

  const confirmChoiceOnEnter = (e) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      confirmChoice();
    }
  };  

  const {
    locationsText,
    radiusValues,
  } = useMemo(
    () => getConfigurationsBasedOnLocations({ searchLocations }),
    [searchLocations],
  );

  return (
    <MapApiInitializer libraries={['marker', 'geometry', 'places', 'geocoding']}>
      <div className={`qbradiusmap is-rel c-bg--w${opaque ? ' qbradiusmap--opaque' : ''} is-anim`}>
        <div className="qbradiusmap__mapcontainer c-bg--f4">
          {componentIsActive ? (
            <Map
              center={center}
              zoom={zoom}
              bounds={bounds}
              baseClassName="qbradiusmap"
              updatequery={() => {}}
              device={device}
              viewport={viewport}
              mobileOs={mobileOs}
              searchMode={DRAWING_ACTIONS_MAP.expandLocalityRadius}
              isSafari={isSafari}
              getAvailableResults={getAvailableResults}
              openQbFiltersPanel={openQbFiltersPanel}
              qbSSearchAction={updateTempSaveSearchStateFromMapPanel}
              hasCollapseColumns={false}
              hasListLink={false}
              hasYouGotAwayMsg={false}
              qbGoToSRP={() => {}}
              drawingData={{
                action: DRAWING_ACTIONS_MAP.expandLocalityRadius,
                localityPolygons: locationPolygons,
                qExpansion: tempFiltersQB.qExpansion,
                changePolygonsIndex,
              }}
            />
          ) : null}
        </div>
        <div className="qbradiusmap__controls">
          <p className="qbradiusmap__controls__text tp-w--m tp-a--c c-txt--f0">Aumenta l’area di ricerca {locationsText} per vedere anche gli immobili nei dintorni</p>
          <Buttons
            filter="radius"
            className="mb--ms"
            value={tempFiltersQB.qExpansion || '0'}
            values={radiusValues}
            update={updateQExpansion}
          />
          <button
            type="button"             
            className="csa_mapsearch__bod__over__layer__bot__btn tp-s--l tp-w--m c-txt--w c-bg--primary b--s c-br--primary tp-a--c is-clickable"
            onClick={confirmChoice}
            onKeyDown={confirmChoiceOnEnter}
            disabled={isFetching}
            aria-live="polite"
          >
            {isFetching ? <Spinner className="tp-va--m" color="w" /> : <>Conferma <span className="tp-w--l">{tempAvailableResultsQB}</span> risultati</>}
          </button>          
        </div>
      </div>
    </MapApiInitializer>
  );
};

export default ExpandLocalityRadius;

ExpandLocalityRadius.propTypes = {
  queryBuilderProps: PropTypes.shape({
    isActivePanel: PropTypes.bool.isRequired,
    customStyle: PropTypes.object,
    updateModalSize: PropTypes.func.isRequired,
    hiddenPanels: PropTypes.array.isRequired,
    panel: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    revealPanelContents: PropTypes.func.isRequired,
    device: PropTypes.string.isRequired,
    viewport: PropTypes.object.isRequired,
    updateTempStateFromExpandRadiusPanel: PropTypes.func.isRequired,
    updateTempSaveSearchStateFromMapPanel: PropTypes.func.isRequired,
    mobileOs: PropTypes.string.isRequired,
    isSafari: PropTypes.bool.isRequired,
    closeModal: PropTypes.func.isRequired,
    panelId: PropTypes.string.isRequired,
    headerTitleId: PropTypes.string.isRequired,
    changeCurrentPanel: PropTypes.func.isRequired,
    confirmTempStateAndGoBack: PropTypes.func.isRequired,
    filters: PropTypes.object.isRequired,
    tempFiltersQB: PropTypes.shape({
      qExpansion: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired,
    tempAvailableResultsQB: PropTypes.string.isRequired,
    tempLocationsQB: PropTypes.shape({
      searchLocations: PropTypes.array.isRequired,
      searchLocationsParent: PropTypes.object.isRequired,
    }).isRequired,
    locationPolygons: PropTypes.array.isRequired,
    setLocationPolygons: PropTypes.func.isRequired,
    isFetching: PropTypes.bool.isRequired,
    confirmChoiceAndClosePanel: PropTypes.func.isRequired,
    paPage: PropTypes.string.isRequired,
  }).isRequired,
  modalData: PropTypes.shape({
    restoreInitialSize: PropTypes.func.isRequired,
  }).isRequired,
};
